import React, { useEffect, useMemo, useState } from 'react'
import { RouteComponentProps, withRouter } from 'react-router'
import { useFaq, useReferralCode } from 'content/api'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import config from 'constants/config'
import { connect } from 'react-redux'
import HeadData from 'components/Common/HeadData'
import HeroImage from 'content/components/HeroImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LoggedInOnly from 'components/Common/LoggedInOnly'
import styled from 'styled-components'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import KeyMessages, { KeyMessage } from 'content/components/KeyMessages'
import PageSection from 'content/components/PageSection'
import LineLinkIcon from 'components/Luxkit/Icons/line/LineLinkIcon'
import TextLink from 'components/Luxkit/TextLink'
import OfferList from 'components/OfferList/OfferList'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import FrequentlyAskedQuestions from 'components/Content/FrequentlyAskedQuestions'
import FormatCurrency from 'components/Common/FormatCurrency'
import { FetchAndDispatchFunction } from 'src/server/state/types'
import { getReferralCodeValidation } from 'api/promo'
import { getLegalPage } from 'api/content'
import { getMaxRefereeProductTypeDiscount } from 'marketing/shared/getMaxRefereeProductTypeDiscount'
import { fetchReferralValue } from 'actions/ReferAction'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'
import ReferralTopLocations from './ReferralTopLocations'
import { useAppDispatch } from 'hooks/reduxHooks'

const howItWorks: Array<KeyMessage> = [{
  icon: 'star',
  title: 'Become a member',
  text: 'Access exclusive offers at membership prices.',
}, {
  icon: 'search',
  title: 'Find your perfect escape',
  text: 'From beach holidays in Bali to European city breaks.',
}, {
  icon: 'mountains-sun',
  title: 'Book your first escape',
  text: 'Enter the unique referal promo code at checkout.',
}]

const CodeBox = styled(Group)`
  width: ${rem(400)};
  max-width: 100%;
  padding: ${rem(8)};
  margin: 0 auto;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  background: ${props => props.theme.palette.neutral.default.eight};
`

const CenteredContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const CenteredVerticalSpacer = styled(VerticalSpacer)`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const EscapeWithUsText = styled(BodyText)`
  max-width: ${rem(948)};
`

const Wrapper = styled.div`
  z-index: ${zIndex.min};
  width: 100%;
  max-width: ${rem(860)};
`

interface RouteProps {
  referralCode: string;
}

interface Props extends RouteComponentProps<RouteProps> {
  memberId: string;
  numberOfPurchases: number;
  regionCode: string
  maxRefereeAmount: number | null;
}

function RefereeLanding(props: Props) {
  const { referralCode } = props.match.params
  const { memberId, numberOfPurchases, maxRefereeAmount, regionCode } = props

  const { isSuccess } = useReferralCode(referralCode)
  const { data: faq } = useFaq('referrer-program-faq')

  const referrerName = useMemo(() => {
    const match = new RegExp('INVITE(.*)(.{6})$').exec(referralCode)
    return match ? match[1].toLowerCase() : 'your friend'
  }, [referralCode])

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchReferralValue(regionCode))
  }, [dispatch, regionCode])

  const [hasCopied, setCopied] = useState<boolean>(false)

  if (!isSuccess) {
    // @TODO: error state for referee page
    <Error404PageWithOffers />
  }

  return (
    <>
      <HeadData
        title="Save money on your first Luxury Escape"
        description="Let Luxury Escapes open up a world of amazing destinations, incredible inclusions and unforgettable experiences"
        imageId="nyaamiuv8bnw1ww4avza2xqifyle"
      />
      <HeroImage
        alt="Beautiful places to visit"
        aria-hidden="true"
        id="gltlms76tqshg8a6agu"
      >
        <Wrapper>
          <VerticalSpacer gap={numberOfPurchases > 0 ? 32 : 16}>
            <VerticalSpacer gap={12}>
              {!memberId && (
                <Heading variant="heading2" as="h1" colour="neutral-eight" align="center" format="propercase">
                  {referrerName} just gave you the ultimate perk!
                </Heading>
              )}
              {memberId && numberOfPurchases === 0 && maxRefereeAmount && (
                <Heading variant="heading2" as="h1" colour="neutral-eight" align="center">
                  Save up to <FormatCurrency value={maxRefereeAmount} /> off your first {config.brandConfig.brandHoliday}
                </Heading>
              )}
              {memberId && numberOfPurchases > 0 && (
                <Heading variant="heading2" as="h1" colour="neutral-eight" align="center">
                  Looks like you've booked an {config.brandConfig.holidaySynonym} before!
                </Heading>
              )}
              {!memberId && maxRefereeAmount && (
                <Subtitle variant="subtitle1" colour="neutral-eight" align="center">
                  Save up to <FormatCurrency value={maxRefereeAmount} /> off your first {config.brandConfig.brandHoliday}
                </Subtitle>
              )}
              {memberId && maxRefereeAmount && numberOfPurchases === 0 && (
                <Subtitle variant="subtitle1" colour="neutral-eight" align="center">
                  Simple copy the code below for up to <FormatCurrency value={maxRefereeAmount} />
                </Subtitle>
              )}
              {memberId && numberOfPurchases > 0 && (
                <Subtitle variant="subtitle1" colour="neutral-eight" align="center">
                  Referral rewards can only be claimed for first time bookings. Start referring your friends to earn more rewards.
                </Subtitle>
              )}
            </VerticalSpacer>
            <CenteredContent>
              <LoggedInOnly buttonText="Become a member to claim" buttonProps={{ size: 'large' }}>
                {numberOfPurchases === 0 && (
                  <VerticalSpacer gap={16}>
                    <Heading variant="heading6" colour="neutral-eight" align="center">
                      {referrerName}'s Promo Code
                    </Heading>
                    <VerticalSpacer gap={32}>
                      <CodeBox direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
                        <BodyText variant="medium" wrap="truncate">
                          {referralCode}
                        </BodyText>
                        <TextButton
                          kind="tertiary"
                          size="large"
                          startIcon={!hasCopied ? <LineLinkIcon size="S" /> : undefined}
                          onClick={() => {
                            copyValueToClipboard(referralCode)
                            setCopied(true)
                            window.setTimeout(() => {
                              setCopied(false)
                            }, 1500)
                          }}
                        >
                          {hasCopied ? 'Copied!' : 'Copy'}
                        </TextButton>
                      </CodeBox>
                      <TextButton kind="primary" size="large" fit="auto" to="/" >
                        Browse all offers
                      </TextButton>
                    </VerticalSpacer>
                  </VerticalSpacer>
                )}
                {numberOfPurchases > 0 && (
                  <TextButton kind="primary" size="large" fit="auto" to="/refer" >
                    How to refer a friend
                  </TextButton>
                )}
              </LoggedInOnly>
            </CenteredContent>
          </VerticalSpacer>
        </Wrapper>
      </HeroImage>
      {!memberId && (
        <PageSection>
          <CenteredVerticalSpacer gap={16}>
            <Heading variant="heading3" align="center">
              It's easy to get started
            </Heading>
            <BodyText variant="large" align="center">
              Simply use your friends referral at checkout and start packing your suitcase. It's that simple.
            </BodyText>
            <KeyMessages items={howItWorks} />
            <BodyText variant="large">
              Read our full <TextLink weight="regular" to="/terms-and-conditions">Terms &amp; Conditions</TextLink>
            </BodyText>
          </CenteredVerticalSpacer>
        </PageSection>
      )}

      <PageSection>
        <CenteredVerticalSpacer gap={40}>
          <Heading variant="heading3">
            Our handpicked escapes
          </Heading>
          <OfferList
            maxOffers={3}
            filters={{
              offerTypes: FLASH_OFFERS_TYPES,
              frontPageVisibleOnly: true,
            }} />
          <TextButton kind="primary" size="large" to="/">
            Browse all offers
          </TextButton>
        </CenteredVerticalSpacer>
      </PageSection>

      {memberId && (
        <PageSection>
          <CenteredVerticalSpacer gap={16}>
            <Heading variant="heading3" align="center">
              It's easy to get started
            </Heading>
            <BodyText variant="large" align="center">
              Simply use your friends referral at checkout and start packing your suitcase. It's that simple.
            </BodyText>
            <KeyMessages items={howItWorks} />
            <BodyText variant="medium">
              Read our full <TextLink weight="regular" to="/terms-and-conditions">Terms &amp; Conditions</TextLink>
            </BodyText>
          </CenteredVerticalSpacer>
        </PageSection>
      )}

      <PageSection>
        <CenteredVerticalSpacer gap={40}>
          <Heading variant="heading3" align="center">
            Escape with us...
          </Heading>
          <EscapeWithUsText variant="large" align="center">
            Let Luxury Escapes open up a world of amazing destinations, incredible inclusions and unforgettable experiences.
            Simply choose a country below and start turning your holiday dreams into reality...
          </EscapeWithUsText>
          <ReferralTopLocations />
          <TextButton kind="primary" size="large" to="/">
            See more destinations
          </TextButton>
        </CenteredVerticalSpacer>
      </PageSection>

      {faq && (
        <PageSection>
          <FrequentlyAskedQuestions
            heading="Frequently asked questions"
            items={faq}
            maxToShow={10}
          />
        </PageSection>
      )}
    </>
  )
}

const mapState = (state: App.State) => {
  return {
    memberId: state.auth.account.memberId,
    regionCode: state.geo.currentRegionCode,
    maxRefereeAmount: state?.refer?.referralValue?.product_type_discount ? getMaxRefereeProductTypeDiscount(state.refer.referralValue.product_type_discount) : null,
    // WTF SVC-AUTH RETURNS NULL
    numberOfPurchases: state.auth.account.numberOfPurchases || 0,
  }
}

export default withRouter(connect(mapState)(RefereeLanding))

const REFEREE_LEGAL_PAGE_SLUG = 'referrer-program-faq'

export const prefetch: FetchAndDispatchFunction<RouteProps> = async({
  match,
  queryClient,
}) => {
  await queryClient.prefetchQuery(['referralCode', match.params.referralCode], () =>
    getReferralCodeValidation(match.params.referralCode),
  )
  await queryClient.prefetchQuery(['faq', REFEREE_LEGAL_PAGE_SLUG], () =>
    getLegalPage(REFEREE_LEGAL_PAGE_SLUG),
  )
}
